import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { useTheme } from '../../ThemeContext';
import { smsAPI } from '../../services/api';
import NotificationBell from './NotificationBell';
import InstallAppItem from '../InstallAppItem/InstallAppItem';
import NotificationsToggle from '../NotificationsToggle/NotificationsToggle';
import './navbar.css';

const Navbar = ({ currentSection, onSectionChange, onNavigateToProject }) => {
  const { user, logout, isAdmin } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [smsUnread, setSmsUnread] = useState(0);
  const userDropdownRef = useRef(null);

  // Poll the SMS inbox unread count for the Messages badge.
  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const res = await smsAPI.unreadCount();
        if (active) setSmsUnread(res.data?.count || 0);
      } catch {
        /* ignore — texting may not be set up yet */
      }
    };
    poll();
    const id = setInterval(poll, 20000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
    setIsUserDropdownOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleSectionClick = (section) => {
    onSectionChange(section);
    closeMenu();
  };

  const isActive = (section) => currentSection === section;

  // Close user dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
        setIsUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="nav-container">
      <div className="navbar-brand" onClick={() => handleSectionClick('tasks')}>
        <img src="/artemis-gold.png" alt="Artemis" className="navbar-logo" />
      </div>

      <div className="navbar-links">
        <ul className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
          <li>
            <button
              className={`nav-link ${isActive('customers') ? 'active' : ''}`}
              onClick={() => handleSectionClick('customers')}
            >
              Contacts
            </button>
            <button
              className={`nav-link ${isActive('projects') ? 'active' : ''}`}
              onClick={() => handleSectionClick('projects')}
            >
              Projects
            </button>
            <button
              className={`nav-link ${isActive('boards') || isActive('board-view') ? 'active' : ''}`}
              onClick={() => handleSectionClick('boards')}
            >
              Boards
            </button>
            <button
              className={`nav-link ${isActive('messages') ? 'active' : ''}`}
              onClick={() => handleSectionClick('messages')}
            >
              Messages
              {smsUnread > 0 && (
                <span
                  style={{
                    marginLeft: 6,
                    background: '#e5534b',
                    color: '#fff',
                    borderRadius: 9,
                    padding: '1px 6px',
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {smsUnread > 9 ? '9+' : smsUnread}
                </span>
              )}
            </button>
          </li>

          <li className="nav-actions">
            <span className="nav-actions-bell">
              <NotificationBell onNavigateToProject={onNavigateToProject} />
            </span>
            <div className="user-dropdown" ref={userDropdownRef}>
              <button
                className="user-dropdown-trigger"
                onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
              >
                <span className="username">{user?.username}</span>
                <span className="user-role">
                  {user?.role}
                </span>
                <span className={`dropdown-chevron ${isUserDropdownOpen ? 'open' : ''}`}>▾</span>
              </button>
              <div className={`user-dropdown-menu ${isUserDropdownOpen ? 'open' : ''}`}>
                  {isAdmin() && (
                    <button
                      className="user-dropdown-item"
                      onClick={() => { handleSectionClick('admin'); setIsUserDropdownOpen(false); }}
                      title="Administrator Only"
                    >
                      Admin
                    </button>
                  )}
                  {isAdmin() && (
                    <button
                      className="user-dropdown-item"
                      onClick={() => { handleSectionClick('automations'); setIsUserDropdownOpen(false); }}
                      title="Administrator Only"
                    >
                      Automations
                    </button>
                  )}
                  {isAdmin() && (
                    <button
                      className="user-dropdown-item"
                      onClick={() => { handleSectionClick('email-templates'); setIsUserDropdownOpen(false); }}
                      title="Administrator Only"
                    >
                      Email Templates
                    </button>
                  )}
                  {isAdmin() && (
                    <button
                      className="user-dropdown-item"
                      onClick={() => { handleSectionClick('document-templates'); setIsUserDropdownOpen(false); }}
                      title="Administrator Only"
                    >
                      Document Templates
                    </button>
                  )}
                  {isAdmin() && (
                    <button
                      className="user-dropdown-item"
                      onClick={() => { handleSectionClick('crews'); setIsUserDropdownOpen(false); }}
                      title="Administrator Only"
                    >
                      Crews
                    </button>
                  )}
                  {isAdmin() && (
                    <button
                      className="user-dropdown-item"
                      onClick={() => { handleSectionClick('lead-sources'); setIsUserDropdownOpen(false); }}
                      title="Administrator Only"
                    >
                      Lead Sources
                    </button>
                  )}
                  {isAdmin() && (
                    <button
                      className="user-dropdown-item"
                      onClick={() => { handleSectionClick('custom-fields'); setIsUserDropdownOpen(false); }}
                      title="Administrator Only"
                    >
                      Custom Fields
                    </button>
                  )}
                  {isAdmin() && <div className="user-dropdown-divider" />}
                  <button
                    className="user-dropdown-item theme-toggle-row"
                    onClick={toggleTheme}
                    aria-pressed={isDark}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                    }}
                  >
                    <span>{isDark ? 'Dark Mode' : 'Light Mode'}</span>
                    <span
                      className="theme-toggle-switch"
                      role="presentation"
                      style={{
                        position: 'relative',
                        width: '36px',
                        height: '20px',
                        borderRadius: '999px',
                        background: isDark ? '#2f81f7' : '#cbd5e1',
                        transition: 'background 150ms ease',
                        flexShrink: 0,
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: '2px',
                          left: isDark ? '18px' : '2px',
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          background: '#fff',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                          transition: 'left 150ms ease',
                        }}
                      />
                    </span>
                  </button>
                  <InstallAppItem onDone={() => setIsUserDropdownOpen(false)} />
                  <NotificationsToggle />
                  <button className="user-dropdown-item" onClick={() => { handleSectionClick('settings'); setIsUserDropdownOpen(false); }}>
                    User Settings
                  </button>
                  <div className="user-dropdown-divider" />
                  <button className="user-dropdown-item logout" onClick={handleLogout}>
                    Logout
                  </button>
                </div>
            </div>
          </li>
        </ul>
      </div>

      <div className="navbar-mobile-actions">
        <NotificationBell onNavigateToProject={onNavigateToProject} />
      </div>

      <div
        className={`hamburger ${isMenuOpen ? 'open' : ''}`}
        onClick={toggleMenu}
      >
        <div className={`line ${isMenuOpen ? 'open' : ''}`}></div>
        <div className={`line ${isMenuOpen ? 'open' : ''}`}></div>
        <div className={`line ${isMenuOpen ? 'open' : ''}`}></div>
      </div>
    </nav>
  );
};

export default Navbar;
